/*!
 * gritCore.js — fold:, clip:, ring:, comb:, crush: -- the sound, sample by sample
 * © 2026 Rob Canning — GPLv3
 *
 *     fold:0.6                        a wavefolder: driven past the edge, the wave folds back in
 *     clip:0.5                        hard clipping: driven into a flat ceiling (drive: is the soft one)
 *     ring:300  ring:{freq:300, mix:0.5}   ring modulation by a sine at freq Hz
 *     comb:110  comb:{freq:110, fb:0.9, mix:1}   a comb filter fed back: it rings at freq
 *     crush:4   crush:{bits:4, rate:3000}      fewer bits, and held at a lower rate (Hz)
 *
 * In that order, after fuzz: and before the envelope (synth.js), or in an fx
 * bus after its fuzz (fxBus.js). Pure: run in the audio engine by
 * gritWorklet.js; testable here. A change (a line run again, a follow())
 * glides there over about 10 ms, so a moving fold does not click; bits and
 * rate step, as they are steps.
 */

const num = (v, d) => (Number.isFinite(Number(v)) && v !== "" && v !== null && typeof v !== "object" ? Number(v) : d);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const obj = v => (v && typeof v === "object" && !Array.isArray(v) && v.type == null ? v : null);

/**
 * What a line's fold: clip: ring: comb: crush: ask for, as numbers (a
 * follow() in one: its `fallback`, until the signal says). Null: none of them.
 */
export function gritSettings(p = {}, fallback = () => undefined) {
  const val = (v, d) => num(v, num(fallback(v), d));
  const ring = obj(p.ring), comb = obj(p.comb), crush = obj(p.crush);
  const s = {
    fold: clamp(val(p.fold, 0), 0, 1),
    clip: clamp(val(p.clip, 0), 0, 1),
    ringFreq: clamp(ring ? val(ring.freq, 300) : val(p.ring, 0), 0, 20000),
    ringMix: p.ring == null ? 0 : clamp(ring ? val(ring.mix, 1) : 1, 0, 1),
    combFreq: clamp(comb ? val(comb.freq, 110) : val(p.comb, 110), 10, 10000),
    combFb: clamp(comb ? val(comb.fb, 0.9) : 0.9, -0.99, 0.99),
    combMix: p.comb == null ? 0 : clamp(comb ? val(comb.mix, 1) : 1, 0, 1),
    bits: clamp(crush ? val(crush.bits, 16) : val(p.crush, 16), 1, 16),
    rate: crush ? clamp(val(crush.rate, 0), 0, 96000) : 0,
  };
  // ring:0 is no ring (a sine at 0 Hz is silence)
  if (!(s.ringFreq > 0)) s.ringMix = 0;
  return gritActive(s) ? s : null;
}

/** Anything to do? */
export const gritActive = s => !!s && (s.fold > 0 || s.clip > 0 || s.ringMix > 0 || s.combMix > 0 || s.bits < 16 || (s.rate > 0 && s.rate < 96000));

export class GritCore {
  constructor(sampleRate, settings = null) {
    this.sr = sampleRate;
    this.glide = 1 - Math.exp(-1 / (0.01 * sampleRate));       // ~10 ms
    this.cur = { fold: 0, clip: 0, ringFreq: 0, ringMix: 0, combFreq: 110, combFb: 0, combMix: 0 };
    this.phase = 0;                                             // the ring's sine
    this.buf = new Float32Array(Math.ceil(sampleRate / 10) + 4); // a comb down to 10 Hz
    this.w = 0;
    this.hold = 0; this.held = 0;                               // the rate's sample-and-hold
    this.set(settings);
    // the first settings straight away: no glide in from nothing
    Object.assign(this.cur, { fold: this.to.fold, clip: this.to.clip, ringFreq: this.to.ringFreq, ringMix: this.to.ringMix, combFreq: this.to.combFreq, combFb: this.to.combFb, combMix: this.to.combMix });
  }

  set(s) {
    this.to = { fold: 0, clip: 0, ringFreq: 0, ringMix: 0, combFreq: 110, combFb: 0, combMix: 0, bits: 16, rate: 0, ...(s || {}) };
  }

  process(input, output) {
    const c = this.cur, t = this.to, k = this.glide, sr = this.sr, buf = this.buf, N = buf.length;
    const q = t.bits < 16 ? Math.pow(2, Math.max(1, Math.round(t.bits)) - 1) : 0;
    const step = t.rate > 0 && t.rate < sr ? t.rate / sr : 0;
    for (let i = 0; i < output.length; i++) {
      c.fold += (t.fold - c.fold) * k;
      c.clip += (t.clip - c.clip) * k;
      c.ringFreq += (t.ringFreq - c.ringFreq) * k;
      c.ringMix += (t.ringMix - c.ringMix) * k;
      c.combFreq += (t.combFreq - c.combFreq) * k;
      c.combFb += (t.combFb - c.combFb) * k;
      c.combMix += (t.combMix - c.combMix) * k;
      let x = input ? input[i] : 0;

      // fold: 1x (as it is, within -1..1) to 10x, folded back off +-1 (a triangle of it)
      if (c.fold > 1e-4) {
        let f = (x * (1 + 9 * c.fold) + 1) * 0.25;
        f -= Math.floor(f);
        x = 1 - 4 * Math.abs(f - 0.5);
      }
      // clip: 1x to 31x, into +-1
      if (c.clip > 1e-4) x = clamp(x * (1 + 30 * c.clip), -1, 1);
      // ring: times a sine, as much as mix says
      if (c.ringMix > 1e-4) {
        this.phase += c.ringFreq / sr;
        if (this.phase >= 1) this.phase -= Math.floor(this.phase);
        x *= 1 - c.ringMix + c.ringMix * Math.sin(2 * Math.PI * this.phase);
      }
      // comb: x + fb times itself a period of freq ago (read between samples), level kept by (1 - |fb|)
      if (c.combMix > 1e-4) {
        const d = clamp(sr / c.combFreq, 1, N - 2);
        let r = this.w - d;
        if (r < 0) r += N;
        const r0 = Math.floor(r), fr = r - r0;
        const back = buf[r0] * (1 - fr) + buf[(r0 + 1) % N] * fr;
        const y = x + c.combFb * back;
        buf[this.w] = Math.abs(y) > 4 ? Math.sign(y) * 4 : y;   // held in, should it run away
        this.w = (this.w + 1) % N;
        x = x * (1 - c.combMix) + y * (1 - Math.abs(c.combFb)) * c.combMix;
      }
      // crush: held at the lower rate, then rounded to the bits
      if (step) {
        this.hold += step;
        if (this.hold >= 1) { this.hold -= 1; this.held = x; }
        x = this.held;
      }
      if (q) x = Math.round(x * q) / q;
      output[i] = x;
    }
  }
}
